import { useEffect,  useState } from 'react'; 
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { isValid as isValidDate } from 'date-fns';
import DataTable from 'react-data-table-component';
import { BASE_URL } from '../utilities/api';
import { useOrder } from '../contexts/OrderContext';
import { useNavigateWithContext } from '../hooks/useNavigateWithContext';
import { useRefillButtonGate } from '../hooks/useRefillButtonGate';
import Spinner from './Spinner';
import { getRefillActionState, getRefillEligibleDate, isUnpaidOrder } from '../utilities/refillRules';
import { findProductByCode, formatDispenseQuantity } from '../utilities/productDisplay';

const Orderstable = () => {
  const { orders, products, refreshOrders, isLoading, error } = useOrder();

  // orders.* doesn't carry route/dispense facets (those live on products,
  // joined by product_code); products is the authenticated list that does.
  const getOrderQuantity = (row) => {
    const product = findProductByCode(products, row.product_code);
    return formatDispenseQuantity(product, row.order_qty)
      || (row.order_qty ? `${row.order_qty} month${String(row.order_qty) === '1' ? '' : 's'}` : 'Not available');
  };
  const [filteredOrders, setFilteredOrders] = useState([]); // Renamed for clarity
  const [mobileVisibleCount, setMobileVisibleCount] = useState(10);
  const navigateWithContext = useNavigateWithContext();
  const { requestRefill, gateModal: refillGateModal } = useRefillButtonGate();

  useEffect(() => {
    if (orders) {
      const filtered = orders;
      setFilteredOrders(filtered);
    }
  }, [orders]);

  useEffect(() => {
    setMobileVisibleCount(10);
  }, [filteredOrders.length]);

  // --- Event Handlers ---
  const handleSearch = (e) => {
    const search = e.target.value.toLowerCase();
    if (!Array.isArray(orders)) {
      setFilteredOrders([]);
      return;
    }

    const searchableColumns = columns.filter(
      col => typeof col.selector === 'function'
    );

    const filtered = orders.filter(row =>
      searchableColumns.some(col => {
        try {
          const raw = col.selector(row);
          if (raw == null) return false;

          // --- If it's a real Date object, compare against both date formats ---
          if (raw instanceof Date && isValidDate(raw)) {
            const iso       = raw.toISOString().slice(0, 10);        // "YYYY-MM-DD"
            const locale    = raw.toLocaleDateString();              // user locale, e.g. "10/31/2024"
            const lcIso     = iso.toLowerCase();                     // already lower, but safe
            const lcLocale  = locale.toLowerCase();
            return (
              lcIso.includes(search)    ||  // e.g. typing "2024-10" or "2024"
              lcLocale.includes(search)     // e.g. typing "10/31" or "oct 31"
            );
          }

          // --- Otherwise just string-match the raw value ---
          const str = String(raw).toLowerCase();
          return str.includes(search);

        } catch {
          return false;
        }
      })
    );

    setFilteredOrders(filtered);
  };


  const handleRefresh = async () => {
     refreshOrders()
  };

  const formatOrderDate = (dateValue) => dateValue ? new Date(dateValue).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }) : 'Not available';

  // [FX-174] Prefer the backend-signed receipt_url (from remote_get_orders);
  // fall back to the legacy raw-id link, which 404s after the 2026-09-01 grace cutoff (#171).
  const getReceiptLink = (row) => row.receipt_url || `${BASE_URL}/viewreceipt/order/${row.order_id}`;

  const renderOrderActions = (row, layout = 'table') => {
    const refillState = getRefillActionState(row, orders);
    const showRefill = refillState.isRefillable;
    const showRefillCountdown = refillState.showRefillCountdown;
    const showPay = refillState.isUnpaid;
    const isPaid = !isUnpaidOrder(row);
    const buttonClass = layout === 'card'
      ? 'flex-1 bg-button text-white px-3 py-2 text-sm rounded hover:bg-button-light text-center'
      : 'w-14 bg-button text-white px-3 py-1 text-sm rounded hover:bg-button-light whitespace-nowrap shrink-0';

    if (!showRefill && !showPay && !showRefillCountdown && !(layout === 'card' && isPaid)) return null;

    return (
      <div className={layout === 'card' ? 'flex flex-wrap items-center gap-2 mt-3' : 'flex w-full items-center justify-center gap-2'}>
        {showRefill && (
          <button
            onClick={() => requestRefill(row.order_id)}
            className={buttonClass}
            title="Refill this order"
          >
            Refill
          </button>
        )}

        {showPay && (
          <button
            onClick={() => navigateWithContext(row.order_id, "/payment", "order")}
            className={buttonClass}
            title="Pay for this order"
          >
            Pay
          </button>
        )}

        {showRefillCountdown && !showPay && (
          <p className="text-xs text-neutral-500 whitespace-nowrap shrink-0 p-1">
            {refillState.daysUntilRefill} day{refillState.daysUntilRefill !== 1 ? 's' : ''} left
          </p>
        )}

        {layout === 'card' && isPaid && (
          <Link
            to={getReceiptLink(row)}
            target="_blank"
            className={layout === 'card' ? 'flex-1 px-3 py-2 text-sm rounded border border-secondary text-secondary text-center hover:bg-secondary hover:text-white' : 'text-secondary hover:underline text-sm'}
          >
            View Receipt
          </Link>
        )}
      </div>
    );
  };

  const renderMobileOrderCard = (row) => {
    const refillDate = getRefillEligibleDate(row);

    return (
      <article key={row.order_id} className="border border-gray-200 rounded bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h5 className="font-semibold text-primary">{row.product_name || 'Medication order'}</h5>
            <p className="text-sm text-neutral-dark">{row.dosage || 'Dose not available'}</p>
          </div>
          <span className="text-xs px-2 py-1 rounded bg-neutral-light text-neutral-dark whitespace-nowrap">
            {row.order_status || 'Status unknown'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm text-neutral-dark">
          <p><span className="block text-xs uppercase text-gray-500">Ordered</span>{formatOrderDate(row.order_date)}</p>
          <p><span className="block text-xs uppercase text-gray-500">Quantity</span>{getOrderQuantity(row)}</p>
          <p><span className="block text-xs uppercase text-gray-500">Refill Eligible</span>{refillDate ? formatOrderDate(refillDate) : 'Not available'}</p>
          <p><span className="block text-xs uppercase text-gray-500">Order ID</span>{row.order_id}</p>
        </div>

        {findProductByCode(products, row.product_code)?.route && (
          <p className="text-sm text-neutral-dark">
            <span className="block text-xs uppercase text-gray-500">Route</span>
            {findProductByCode(products, row.product_code).route}
          </p>
        )}

        {row.directions && (
          <p className="text-sm text-neutral-dark">
            <span className="block text-xs uppercase text-gray-500">Directions</span>
            {row.directions}
          </p>
        )}

        {renderOrderActions(row, 'card')}
      </article>
    );
  };

  // --- Column Definitions ---
  const columns = [
    {
      name: 'Refill',
      cell: row => renderOrderActions(row),
      ignoreRowClick: true,
      width: '10em', // Adjusted width slightly
    },
    {
      name: 'Order Date',
      selector: row => row.order_date, // Use order_date for sorting
      sortable: true,
      cell: row => formatOrderDate(row.order_date),
      width: '8em',
    },
    {
      name: 'Drug Name',
      selector: row => row.product_name, // Use product_name for sorting/selector
      sortable: true,
      // maxWidth: '25em', // Removed maxWidth for better wrapping
      wrap: true,
      cell: row => (
        // Removed fixed width to allow natural wrapping
        <div>{row.product_name}</div>
      ),
    },
    {
      name: 'Dosage',
      selector: row => row.dosage,
      sortable: true,
      width: '8em', // Adjusted width
    },
    {
      name: 'Directions',
      selector: row => row.directions,
      sortable: true,
      wrap: true,
      cell: row => (
        // Show on medium screens and up
        <div className="hidden md:block">{row.directions}</div>
      ),
    },
    {
      name: 'Qty',
      selector: row => row.order_qty,
      sortable: true,
      width: '8em',
      cell: row => (
        <div className="hidden md:block">{getOrderQuantity(row)}</div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.order_status,
      sortable: true,
      width: '8em', // Adjusted width
      cell: row => (
        <div className="hidden md:block">{row.order_status}</div>
      ),
    },
    {
      name: 'Refill Dt', // Keep the header name
      selector: row => row.next_refill,
      sortable: true,
      cell: row => {
        const refillDate = getRefillEligibleDate(row);
        return (
          <div className="w-full text-center">
            {refillDate ? formatOrderDate(refillDate) : 'Not available'}
          </div>
        );
      },
      width: '8em', // Keep width or adjust as needed
    },
    {
      name: '',
      cell: (row) => {
        return (
          <>
            {!isUnpaidOrder(row) && (
              <p className="text-sm mb-0 text-neutral-500 whitespace-nowrap shrink-0 p-0">
                <Link
                  to={getReceiptLink(row)}
                  target="_blank"
                  className="text-secondary hover:underline"
                >
                  View Receipt
                </Link>
              </p>
            )}
          </>
        );
      },
    },
  ];

  // --- Custom Styles ---
  const customStyles = {
    table: {
      style: {
        // display: 'inline-block', // Removed inline-block for better responsiveness
        // width: 'auto', // Removed auto width
        // '@media (min-width: 768px)': { // Removed media query here, handled by column visibility
        //   width: '100%',
        // },
      },
    },
    headRow: {
      style: {
        backgroundColor: '#E5E7EB', // Example: Light gray background for header
        color: '#1F2937', // Example: Darker text color
        borderBottom: '2px solid #D1D5DB', // Example: Border
        // display: 'flex', // Default DataTable behavior
        // flexWrap: 'nowrap', // Default DataTable behavior
        // width: '100%', // Default DataTable behavior
        // position: 'sticky', // Removed sticky header for simplicity, can be added back if needed
        // top: 0,
        // zIndex: 10,
      },
    },
    headCells: {
      style: {
        fontSize: '0.875rem', // Slightly smaller header font
        fontWeight: '600',
        paddingLeft: '8px',
        paddingRight: '8px',
        // Removed responsive display logic, handled by column definitions now
      },
    },
    rows: {
      style: {
        minHeight: '48px', // Ensure rows have a minimum height
        '&:nth-of-type(odd)': {
            backgroundColor: '#F9FAFB', // Example: Zebra striping
        },
        '&:hover': {
            backgroundColor: '#EFF6FF', // Example: Hover effect
        },
        // display: 'flex', // Default DataTable behavior
        // flexWrap: 'nowrap', // Default DataTable behavior
        // width: '100%', // Default DataTable behavior
      },
    },
    cells: {
      style: {
        fontSize: '0.875rem', // Consistent font size with header
        paddingLeft: '8px',
        paddingRight: '8px',
        // Removed flex properties, rely on DataTable defaults
      },
    },
  };

  // --- Render ---
  return (
    <div className="mb-4 flex flex-col h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-2xl text-primary font-semibold">All Orders</h4>
        <button
          className="text-xl text-gray-400 hover:text-primary-light"
          onClick={handleRefresh}
          title="Refresh Orders"
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
        </button>
      </div>

      {/* Loading State */}
      {isLoading && <Spinner  size="md" overlay={true} />}

      {/* Error State */}
      {!isLoading && error && <p className="text-red-500">{error}</p>}

      {/* Data Table Section (only render if not loading and no error) */}
      {!isLoading && !error && (
        <>
          {/* Search and Info Bar */}
          <div className="flex mb-2 w-full h-auto bg-gray-50 p-2 rounded border border-gray-200 items-center">
            <input
              type="text"
              placeholder="Search orders...  use yyyy-mm-dd or m/d/y for dates"
              className="flex-grow p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-light text-sm"
              onChange={handleSearch}
            />
            <div className="ml-4 text-sm text-gray-600 whitespace-nowrap">
              Showing {filteredOrders.length} of {orders.length}
            </div>
          </div>

          {/* Table Container */}
          <div className="md:hidden space-y-3">
            {orders.length > 0 ? (
              filteredOrders.length > 0 ? (
                <>
                  {filteredOrders.slice(0, mobileVisibleCount).map(renderMobileOrderCard)}
                  {filteredOrders.length > mobileVisibleCount && (
                    <button
                      type="button"
                      onClick={() => setMobileVisibleCount(count => count + 10)}
                      className="w-full px-4 py-2 border border-primary text-primary rounded hover:bg-primary hover:text-white transition duration-200"
                    >
                      Show 10 more
                    </button>
                  )}
                </>
              ) : (
                <p className="text-center p-4">No matching orders found for your search.</p>
              )
            ) : (
              <p className="text-center p-4">No orders found.</p>
            )}
          </div>

          <div className="hidden md:block overflow-x-auto"> {/* Added overflow-x-auto for horizontal scrolling if needed */}
            {orders.length > 0 ? (
              filteredOrders.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={filteredOrders}
                  pagination={true}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]} // Added more options
                  highlightOnHover
                  sortable
                  customStyles={customStyles}
                  responsive // Added for better mobile handling by DataTable
                  striped // Optional: Add zebra striping
                />
              ) : (
                <p className="text-center p-4">No matching orders found for your search.</p>
              )
            ) : (
              <p className="text-center p-4">No orders found.</p>
            )}
          </div>
        </>
      )}
      {refillGateModal}
    </div>
  );
};

export default Orderstable;
